import { provideQuillConfig, QuillModule } from 'ngx-quill';
import Lara from '@primeuix/themes/lara';
import {
    APP_INITIALIZER,
    ErrorHandler,
    NgModule,
    CUSTOM_ELEMENTS_SCHEMA,
} from "@angular/core";
import { Router } from "@angular/router";

import { BrowserModule } from "@angular/platform-browser";
import { PlatformModule } from "@angular/cdk/platform";
import { CommonModule } from "@angular/common";

import { FormsModule, ReactiveFormsModule } from "@angular/forms";

import { AppRoutingModule } from "./app-routing.module";
import { AppComponent } from "./app.component";
import { ErrorComponent } from "./views/error/error.component";
import { EquipmentsComponent } from "./views/equipments/equipments.component";

import { BackendService } from "./services/backend/backend.service";
import { SvgService } from "./services/svg/svg.service";
import { NotificationService } from "./services/notification/notification.service";
import { HttpBaseService } from "./services/http/http-base.service";
import { NewRelicErrorHandler } from "./services/error-handler/new-relic-error-handler";

import {
    provideHttpClient,
    withInterceptorsFromDi,
} from "@angular/common/http";

import { MatIconModule } from "@angular/material/icon";
import { MessageService } from "primeng/api";
import { ToastModule } from "primeng/toast";
import { TooltipModule } from "primeng/tooltip";
import { DialogModule } from "primeng/dialog";
import { FullCalendarModule } from "@fullcalendar/angular"; // must go before plugins
import { SiteFilterComponent } from "./components/site-filter/site-filter.component";
import { BrowserAnimationsModule, NoopAnimationsModule } from "@angular/platform-browser/animations";

import { ProgressSpinnerModule } from "primeng/progressspinner";
import { TableModule } from "primeng/table";
import { LayoutModule } from "@angular/cdk/layout";
import { ListboxModule } from "primeng/listbox";
import { CheckboxModule } from "primeng/checkbox";
import { MultiSelectModule } from "primeng/multiselect";
import { FloatLabelModule } from 'primeng/floatlabel';
import { ConfirmDialogModule } from "primeng/confirmdialog";
import { ConfirmationService } from "primeng/api";

import { ZonesComponent } from "./views/zones/zones.component";
import { VhMobileFixDirective } from "./directives/vh-mobile-fix.directive";
import { InputTextModule } from "primeng/inputtext";
import { TreeModule } from "primeng/tree";
import { AccordionModule } from "primeng/accordion";
import { HistoricalDataComponent } from "./components/historical-data/historical-data.component";
import { provideHighcharts, providePartialHighcharts } from "highcharts-angular";
import { ClipboardModule } from "@angular/cdk/clipboard";
import { ShareLinkComponent } from "./components/share-link/share-link.component";
import { QrCodeModule } from "ng-qrcode";
import { NoAccessComponent } from "./views/no-access/no-access.component";
import { ScheduleCalendarComponent } from "./components/schedule-calendar/schedule-calendar.component";
import { ExceptionScheduleTableComponent } from "./components/exception-schedule-table/exception-schedule-table.component";
import { TermsAndConditionsComponent } from "./views/terms-and-conditions/terms-and-conditions.component";
import { CanDeactivateGuard } from "./services/can-deactivate-guard.service";
import { CustomerSpecificationComponent } from "./views/customer-specification/customer-specification.component";

import { SafeHtmlPipe } from "./pipes/safe-html.pipe";

import { LocationsComponent } from "./views/locations/locations.component";
import { CollectionsComponent } from "./views/collections/collections.component";
import { EquipmentsV2Component } from "./views/equipments-v2/equipments-v2.component";
import { EquipmentDetailsV2Component } from "./views/equipment-details-v2/equipment-details-v2.component";
import { FilterAndSearchComponent } from "./components/filter-and-search/filter-and-search.component";
import { EntityPointsComponent } from "./components/entity-points/entity-points.component";
import { EquipmentDetailsViewWrapperComponent } from "./views/equipments/details-view-wrapper/details-view-wrapper.component";
import { EquipmentDetailsViewComponent } from "./views/equipments/details-view/details-view.component";
import { EquipmentPinnedViewComponent } from "./views/equipments/pinned-view/pinned-view.component";
import { CollectionDetailsViewComponent } from "./views/collections/details-view/details-view.component";
import { CollectionDetailsViewWrapperComponent } from "./views/collections/details-view-wrapper/details-view-wrapper.component";
import { CollectionPinnedViewComponent } from "./views/collections/pinned-view/pinned-view.component";
import { LocationDetailsViewComponent } from "./views/locations/details-view/details-view.component";
import { LocationDetailsViewWrapperComponent } from "./views/locations/details-view-wrapper/details-view-wrapper.component";
import { LocationPinnedViewComponent } from "./views/locations/pinned-view/pinned-view.component";
import { OAuthModule } from "angular-oauth2-oidc";
import {SmartAlertsComponent} from "./components/smart-alerts/smart-alerts.component";
import {DialogService} from "primeng/dynamicdialog";
import {NgOptimizedImage} from "@angular/common";
import {EntityCardComponent} from "./components/entity-card/entity-card.component";
import { ButtonDirective, ButtonModule } from "primeng/button";
import { SmartAlertCodePipe } from "./pipes/smart-alert-code.pipe";
import { AlertStatusLabelPipe } from "./pipes/alert-status-label.pipe";
import { CloseAlertConfirmationPopupComponent } from "./popups/confirmation-popup/close-alert-confirmation-popup.component";
import { MatDialogModule } from "@angular/material/dialog";
import { SiteOverviewComponent } from "./views/site-overview/site-overview.component";
import { EditBuildingOpeningTimeComponent } from "./dialogs/edit-building-opening-time/edit-building-opening-time.component";
import { PreventDashDirective } from "./directives/prevent-dash.directive";
import { ManageOverrideDialogComponent } from "./dialogs/manage-override-dialog/manage-override-dialog.component";
import { HttpClientModule } from '@angular/common/http';
import { InputNumberModule } from "primeng/inputnumber";
import { providePrimeNG } from "primeng/config";
import { PopoverModule } from 'primeng/popover';
import { TextareaModule } from 'primeng/textarea';
import { DatePickerModule } from 'primeng/datepicker';
import { HighchartsChartComponent } from 'highcharts-angular';
import { TabsModule } from 'primeng/tabs';
import { SelectModule } from 'primeng/select';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { TextEditorComponent } from './components/text-editor/text-editor.component';
import { QueryBuilderComponent } from './components/query-builder/query-builder.component';
import { DateInSiteTimeZonePipe } from './pipes/date-in-site-time-zone.pipe';
import { SmartAlertDetailsComponent } from './dialogs/smart-alert-details/smart-alert-details.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
export function checkForErrorParams(router: Router) {
    return () => {
        const urlParams = new URLSearchParams(window.location.search);
        if (
            urlParams.get("error") === "invalid_request" &&
            urlParams.get("error_description")?.includes("invitation")
        ) {
            router.navigate(["/registration/error"]);
        }
        return Promise.resolve();
    };
}

@NgModule({
    declarations: [
        AppComponent,
        ErrorComponent,
        EquipmentsComponent,
        EntityCardComponent,
        SiteFilterComponent,
        ZonesComponent,
        VhMobileFixDirective,
        HistoricalDataComponent,
        ShareLinkComponent,
        NoAccessComponent,
        ScheduleCalendarComponent,
        ExceptionScheduleTableComponent,
        TermsAndConditionsComponent,
        CustomerSpecificationComponent,
        SafeHtmlPipe,
        AlertStatusLabelPipe,
        SmartAlertCodePipe,
        LocationsComponent,
        SiteOverviewComponent,
        CollectionsComponent,
        EquipmentsV2Component,
        EquipmentDetailsV2Component,
        FilterAndSearchComponent,
        EntityPointsComponent,
        EquipmentDetailsViewWrapperComponent,
        EquipmentDetailsViewComponent,
        EquipmentPinnedViewComponent,
        CollectionDetailsViewWrapperComponent,
        CollectionDetailsViewComponent,
        CollectionPinnedViewComponent,
        LocationDetailsViewComponent,
        LocationDetailsViewWrapperComponent,
        LocationPinnedViewComponent,
        SmartAlertsComponent,
        EditBuildingOpeningTimeComponent,
        PreventDashDirective,
        ManageOverrideDialogComponent,
        TextEditorComponent,
        SmartAlertDetailsComponent
    ],
    imports: [
        LayoutModule,
        FormsModule,
        ButtonDirective,
        QueryBuilderComponent,
        DateInSiteTimeZonePipe,
        ReactiveFormsModule,
        BrowserModule,
        CommonModule,
        AppRoutingModule,
        NoopAnimationsModule,
        ListboxModule,
        MatIconModule,
        CheckboxModule,
        InputNumberModule,
        MultiSelectModule,
        InputTextModule,
        TreeModule,
        AccordionModule,
        ClipboardModule,
        QrCodeModule,
        ToastModule,
        TooltipModule,
        ProgressSpinnerModule,
        DialogModule,
        FullCalendarModule,
        TableModule,
        PlatformModule,
        ConfirmDialogModule,
        CloseAlertConfirmationPopupComponent,
        MatDialogModule,
        BrowserAnimationsModule,
        HttpClientModule,
        PopoverModule,
        TextareaModule,
        DatePickerModule,
        HighchartsChartComponent,
        TabsModule,
        SelectModule,
        FloatLabelModule,
        ToggleSwitchModule,
        ButtonModule,
        NgOptimizedImage,
        MatProgressSpinnerModule,
        OAuthModule.forRoot(),
        QuillModule.forRoot()
            // {
            //     resourceServer: {
            //         allowedUrls: ['https://api.dev.cbms.eon-optimum.com', 'https://api.dev.cbms.eon-optimum.com/admin-be'],
            //         sendAccessToken: true,
            //     },
            // }
    ],
    providers: [
        AlertStatusLabelPipe,
        SmartAlertCodePipe,
        provideQuillConfig({
            theme: 'snow'
        }),
        providePartialHighcharts({ modules: () => [import('highcharts/esm/modules/stock')]}),
        provideHighcharts(),
        {
          provide: APP_INITIALIZER,
          useFactory: checkForErrorParams,
          deps: [Router],
          multi: true
        },
        providePrimeNG({
            theme: {
                preset: Lara,
                options: {
                    darkModeSelector: 'none'
                }
            }
        }),
        BackendService,
        SvgService,
        HttpBaseService,
        MessageService,
        ConfirmationService,
        NotificationService,
        CanDeactivateGuard,
        provideHttpClient(withInterceptorsFromDi()),
        DialogService,
        { provide: ErrorHandler, useClass: NewRelicErrorHandler },
    ],
    bootstrap: [AppComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class AppModule {}
